import React, { Fragment, ReactNode } from "react";

import { UserType } from "@types-runway/mdi";
import { IdentityVerificationStatus } from "@types-runway/user";

// Single greedy quantifier per pattern, not two adjacent variable-length
// classes over overlapping charsets. That shape (this file used to exclude
// trailing sentence punctuation inline, e.g. `[^\s<]+[^\s<.,:;!?'")\]}]`) is
// a catastrophic-backtracking hazard: for a long run of "borderline"
// trailing characters, the engine can retry exponentially many ways to
// divide greedy-class vs required-final-char. Trailing punctuation is
// stripped afterward by a plain, linear string operation instead — see
// `splitTrailingPunctuation`.
const URL_PATTERN = /(https?:\/\/[^\s<]+)/gi;
const IS_URL = /^https?:\/\//i;

const EMAIL_PATTERN = /([\w.+-]+@[\w.-]+)/g;
const IS_EMAIL = /^[\w.+-]+@/;

// Punctuation that shouldn't be pulled into a link when it's the last thing
// matched (a period ending a sentence, a closing quote/paren wrapping the
// URL, etc). Anchored to the end of an already-matched string, so this is a
// single linear pass, not a source of backtracking.
const TRAILING_PUNCTUATION = /[.,:;!?'")\]}]+$/;

function splitTrailingPunctuation(match: string): [core: string, suffix: string] {
  const core = match.replace(TRAILING_PUNCTUATION, "");
  return core ? [core, match.slice(core.length)] : [match, ""];
}

// Exact-hostname allowlist, not a substring/suffix check on the raw URL —
// `url.includes("mdintegrations.com")` would also match
// "https://mdintegrations.com.evil.com" or "https://evil.com/mdintegrations.com".
// Only mask a link when `new URL(...).hostname` is an exact match, and only
// add a host here once we've actually observed MD Integrations send links
// from it (today: the `patient` subdomain, from the drivers_license_requested
// / intro_video_requested workflow webhooks).
const MDI_UPLOAD_HOSTS = new Set(["patient.mdintegrations.com"]);

function isTrustedMdiUploadLink(url: string): boolean {
  try {
    return MDI_UPLOAD_HOSTS.has(new URL(url).hostname.toLowerCase());
  } catch {
    return false;
  }
}

// MD Integrations sends these as fixed-template system messages, one
// upload-type request per message/link. Matched against the text
// immediately preceding a given link — never the whole message — so a
// message that touches more than one link/topic can't leak one link's label
// onto a different, unrelated link (e.g. a clinician mentioning "a video" in
// one sentence and pasting an unrelated document link in the next). Local
// context with no recognized template falls back to `null` and the caller
// shows the plain URL — masking with an invented label would be worse than
// not masking at all.
type MdiUploadRequestKind = "driversLicense" | "introVideo";

const DRIVERS_LICENSE_UPLOAD_LABEL = "Upload your driver's license photo";

const MDI_UPLOAD_REQUEST_TEMPLATES: Array<{
  matches: RegExp;
  label: string;
  kind: MdiUploadRequestKind;
}> = [
  {
    matches: /driver.?s?\s*licen[sc]e/i,
    label: DRIVERS_LICENSE_UPLOAD_LABEL,
    kind: "driversLicense",
  },
  {
    matches: /\bvideo\b/i,
    label: "Send us a short video",
    kind: "introVideo",
  },
];

function describeMdiUploadRequest(
  precedingText: string
): { label: string; kind: MdiUploadRequestKind } | null {
  const template = MDI_UPLOAD_REQUEST_TEMPLATES.find(({ matches }) =>
    matches.test(precedingText)
  );
  return template ? { label: template.label, kind: template.kind } : null;
}

// MDI's driver's-license-request message is always a single fixed template
// devoted to that one purpose (verified against a live API response), so
// once we've spotted it, replacing the *entire* message is safe — there's
// nothing else in it to preserve.
function findTrustedDriversLicenseLink(text: string): string | null {
  const parts = text.split(URL_PATTERN);
  for (let index = 0; index < parts.length; index++) {
    if (!IS_URL.test(parts[index])) {
      continue;
    }
    const [url] = splitTrailingPunctuation(parts[index]);
    const precedingText = parts[index - 1] ?? "";
    if (
      isTrustedMdiUploadLink(url) &&
      describeMdiUploadRequest(precedingText)?.kind === "driversLicense"
    ) {
      return url;
    }
  }
  return null;
}

// Rewrites a whole driver's-license-request system message into copy that
// reflects what the patient actually needs to know right now, instead of
// showing MDI's own wording with just the link relabeled. Returns `null`
// when the status doesn't call for a rewrite (`not_requested`, or
// `awaiting_submission` with no panel to open) — the caller then falls
// through to the ordinary per-link masking below.
function renderDriversLicenseSystemMessage(
  text: string,
  options: LinkifyTextOptions
): ReactNode | null {
  if (options.userType !== UserType.SYSTEM) {
    return null;
  }

  const url = findTrustedDriversLicenseLink(text);
  if (!url) {
    return null;
  }

  if (options.identityVerificationStatus === "confirmed") {
    return "Your driver's license has been verified. Thanks for sending that over!";
  }

  if (options.identityVerificationStatus === "awaiting_confirmation") {
    return "We've received your driver's license photo and are reviewing it now.";
  }

  const openPanel = options.onOpenIdentityVerificationPanel;
  if (options.identityVerificationStatus === "awaiting_submission" && openPanel) {
    return (
      <Fragment>
        {
          "Your physician requires a photo of your ID for patient verification before your consultation can be finalized — "
        }
        <a
          href="#"
          onClick={(event) => {
            event.preventDefault();
            openPanel();
          }}
        >
          {DRIVERS_LICENSE_UPLOAD_LABEL}
        </a>
        .
      </Fragment>
    );
  }

  return null;
}

function linkifyEmails(text: string, keyPrefix: string): ReactNode {
  return text.split(EMAIL_PATTERN).map((part, index) => {
    if (!IS_EMAIL.test(part)) {
      return <Fragment key={`${keyPrefix}-plain-${index}`}>{part}</Fragment>;
    }

    const [email, suffix] = splitTrailingPunctuation(part);
    return (
      <Fragment key={`${keyPrefix}-email-${index}`}>
        <a href={`mailto:${email}`}>{email}</a>
        {suffix}
      </Fragment>
    );
  });
}

export type LinkifyTextOptions = {
  userType?: UserType;
  identityVerificationStatus?: IdentityVerificationStatus;
  onOpenIdentityVerificationPanel?: () => void;
};

/**
 * Splits plain text on bare URLs and email addresses, wrapping URLs in a
 * clickable new-tab anchor and emails in a `mailto:` link.
 *
 * A driver's-license-request system message is additionally rewritten as a
 * whole into status-appropriate copy (with an in-app action instead of a
 * link, where one applies) — see "MDI System Messages" in
 * docs/architecture/messaging-realtime.md for the full gating rules and why
 * they exist.
 *
 * URLs are detected first; email matching only runs on the plain-text
 * segments left over, so an email-shaped substring inside a URL's query
 * string (e.g. `?email=foo@bar.com`) stays part of that URL link instead of
 * being split out on its own.
 */
export function linkifyText(text: string, options: LinkifyTextOptions = {}): ReactNode {
  if (!text) {
    return text;
  }

  const driversLicenseMessage = renderDriversLicenseSystemMessage(text, options);
  if (driversLicenseMessage !== null) {
    return driversLicenseMessage;
  }

  const parts = text.split(URL_PATTERN);

  return parts.map((part, index) => {
    if (!IS_URL.test(part)) {
      return (
        <Fragment key={`text-${index}`}>{linkifyEmails(part, `text-${index}`)}</Fragment>
      );
    }

    const [url, suffix] = splitTrailingPunctuation(part);
    const precedingText = parts[index - 1] ?? "";
    // Only ever attempt template matching against MDI's own automated
    // messages — never against a clinician's typed text, which could
    // coincidentally use the same keywords in an unrelated sentence.
    const uploadRequest =
      options.userType === UserType.SYSTEM ? describeMdiUploadRequest(precedingText) : null;
    const isTrustedLink = isTrustedMdiUploadLink(url);
    const label = uploadRequest && isTrustedLink ? uploadRequest.label : url;

    return (
      <Fragment key={`link-${index}`}>
        <a href={url} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
        {suffix}
      </Fragment>
    );
  });
}
